import type { App } from 'vue'
import posthog from 'posthog-js'

type TelemetryProperties = Record<string, unknown>

interface IdentifyUserPayload {
  username: string
  roles?: string[]
}

function getRuntimeMetadata(): TelemetryProperties {
  if (typeof window === 'undefined') {
    return {
      runtime_host: '',
      runtime_origin: '',
      runtime_path: ''
    }
  }

  return {
    runtime_host: window.location.host,
    runtime_origin: window.location.origin,
    runtime_path: window.location.pathname
  }
}

function getBuildMetadata(): TelemetryProperties {
  return {
    app_version: import.meta.env.VITE_APP_VERSION || '',
    app_commit_hash: import.meta.env.VITE_APP_COMMIT_HASH || '',
    app_commit_date: import.meta.env.VITE_APP_COMMIT_DATE || '',
    app_country: import.meta.env.VITE_COUNTRY || '',
    app_title: import.meta.env.VITE_APP_TITLE || '',
    build_base_api: import.meta.env.VITE_BASE_API || ''
  }
}

function getTelemetryMetadata(): TelemetryProperties {
  return {
    ...getBuildMetadata(),
    ...getRuntimeMetadata()
  }
}

let globalErrorHandlersAttached = false

export function initPosthog(): void {
  posthog.init(import.meta.env.VITE_POSTHOG_PROJECT_TOKEN || '', {
    api_host: import.meta.env.VITE_POSTHOG_HOST || '/e',
    ui_host: 'https://eu.posthog.com',
    defaults: '2026-01-30',
    capture_exceptions: true,
    disable_session_recording: false
  })

  posthog.register(getTelemetryMetadata())

  if (globalErrorHandlersAttached || typeof window === 'undefined') {
    return
  }

  window.addEventListener('error', event => {
    captureError(event.error || new Error(event.message), {
      source: 'window.error'
    })
  })

  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    captureError(reason, {
      source: 'window.unhandledrejection'
    })
  })

  globalErrorHandlersAttached = true
}

export function setupVueErrorTracking(app: App): void {
  app.config.errorHandler = (err, instance, info) => {
    captureError(err, {
      source: 'vue',
      component_name: instance?.$options?.name,
      error_info: info
    })
    console.error(err, info)
  }
}

export function identifyUser({ username, roles = [] }: IdentifyUserPayload): void {
  posthog.identify(username, {
    username,
    roles,
    ...getTelemetryMetadata()
  })
}

export function resetUser(): void {
  posthog.reset()
}

export function captureEvent(eventName: string, properties: TelemetryProperties = {}): void {
  posthog.capture(eventName, {
    ...getTelemetryMetadata(),
    ...properties
  })
}

export function captureError(error: unknown, properties: TelemetryProperties = {}): void {
  posthog.captureException(error, {
    ...getTelemetryMetadata(),
    ...properties
  })
}
